import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-subtle bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-blue";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint";
const btnCls = "rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors";
const EMPTY = {
  code: "", discount_type: "percent", discount_value: "", start_date: "", expiry_date: "",
  max_uses: "", one_per_customer: false, min_service_value: "", active: true,
};
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

function statusOf(p) {
  if (!p.active) return ["Inactive", "bg-subtle text-faint"];
  if (p.expiry_date && today() > p.expiry_date) return ["Expired", "bg-red-100 text-red-700"];
  if (p.start_date && today() < p.start_date) return ["Scheduled", "bg-blue/10 text-blue"];
  if (p.max_uses && p.times_used >= p.max_uses) return ["Limit reached", "bg-subtle text-faint"];
  return ["Active", "bg-sage-light text-sage-dark"];
}

function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className={labelCls}>{label}</span>{children}</label>;
}

function PromoForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [error, setError] = useState("");
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const submit = async () => {
    setError("");
    const body = {
      ...f, code: f.code.trim().toUpperCase(), discount_value: Number(f.discount_value),
      start_date: f.start_date || null, expiry_date: f.expiry_date || null,
      max_uses: f.max_uses === "" || f.max_uses == null ? null : Number(f.max_uses),
      min_service_value: f.min_service_value === "" || f.min_service_value == null ? null : Number(f.min_service_value),
    };
    try { await onSave(body); } catch (err) { setError(apiError(err)); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-subtle bg-surface p-6" data-testid="promo-form">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Code"><input className={`${inputCls} uppercase`} value={f.code} placeholder="CHRISTMAS15" onChange={(e) => set({ code: e.target.value })} data-testid="promo-form-code" /></Field>
        <Field label="Discount type">
          <select className={inputCls} value={f.discount_type} onChange={(e) => set({ discount_type: e.target.value })} data-testid="promo-form-type">
            <option value="percent">Percentage (%)</option>
            <option value="fixed">Fixed amount (£)</option>
          </select>
        </Field>
        <Field label={f.discount_type === "percent" ? "Discount value (%)" : "Discount value (£)"}>
          <input type="number" min="0" step="0.01" className={inputCls} value={f.discount_value} onChange={(e) => set({ discount_value: e.target.value })} data-testid="promo-form-value" />
        </Field>
        <Field label="Start date (optional)"><input type="date" className={inputCls} value={f.start_date || ""} onChange={(e) => set({ start_date: e.target.value })} data-testid="promo-form-start" /></Field>
        <Field label="Expiry date (optional)"><input type="date" className={inputCls} value={f.expiry_date || ""} onChange={(e) => set({ expiry_date: e.target.value })} data-testid="promo-form-expiry" /></Field>
        <Field label="Maximum uses (optional)"><input type="number" min="1" className={inputCls} value={f.max_uses ?? ""} onChange={(e) => set({ max_uses: e.target.value })} data-testid="promo-form-max-uses" /></Field>
        <Field label="Minimum cleaning value £ (optional)"><input type="number" min="0" step="0.01" className={inputCls} value={f.min_service_value ?? ""} onChange={(e) => set({ min_service_value: e.target.value })} data-testid="promo-form-min-value" /></Field>
        <Field label="Status">
          <select className={inputCls} value={f.active ? "active" : "inactive"} onChange={(e) => set({ active: e.target.value === "active" })} data-testid="promo-form-status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </Field>
        <label className="flex items-center gap-3 self-end pb-2.5 text-sm text-ink">
          <input type="checkbox" className="h-4 w-4 accent-[#0F2942]" checked={f.one_per_customer} onChange={(e) => set({ one_per_customer: e.target.checked })} data-testid="promo-form-one-per-customer" />
          One use per customer (by email)
        </label>
      </div>
      {error && <p className="mt-4 text-sm text-red-700" data-testid="promo-form-error">{error}</p>}
      <div className="mt-5 flex gap-2">
        <button onClick={submit} data-testid="promo-form-save" className={`${btnCls} bg-blue text-canvas hover:bg-blue-hover`}>Save code</button>
        <button onClick={onCancel} data-testid="promo-form-cancel" className={`${btnCls} border border-subtle text-ink hover:border-blue`}>Cancel</button>
      </div>
    </div>
  );
}

function PromoRow({ p, onEdit, onToggle, onDelete, onCopy }) {
  const [label, cls] = statusOf(p);
  const value = p.discount_type === "percent" ? `${p.discount_value}%` : gbp(p.discount_value);
  const rules = [
    p.start_date && `From ${p.start_date}`, p.expiry_date && `Until ${p.expiry_date}`,
    p.min_service_value && `Min. cleaning ${gbp(p.min_service_value)}`, p.one_per_customer && "One per customer",
  ].filter(Boolean).join(" · ");
  return (
    <div className="grid gap-3 border-b border-subtle px-5 py-4 last:border-0 lg:grid-cols-[1fr_120px_110px_auto] lg:items-center" data-testid={`promo-row-${p.code}`}>
      <div>
        <span className="font-mono text-sm tracking-[0.08em] text-blue">{p.code}</span>
        <span className="ml-3 text-sm text-ink">{value} off cleaning</span>
        {rules && <p className="mt-1 text-xs text-faint">{rules}</p>}
      </div>
      <span className="text-sm text-body" data-testid={`promo-uses-${p.code}`}>Used {p.times_used || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}</span>
      <span className={`w-fit rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${cls}`} data-testid={`promo-status-${p.code}`}>{label}</span>
      <div className="flex flex-wrap gap-2">
        <button onClick={onCopy} data-testid={`promo-copy-link-${p.code}`} className={`${btnCls} border border-subtle text-ink hover:border-blue`}>Copy link</button>
        <button onClick={onEdit} data-testid={`promo-edit-${p.code}`} className={`${btnCls} border border-subtle text-ink hover:border-blue`}>Edit</button>
        <button onClick={onToggle} data-testid={`promo-toggle-${p.code}`} className={`${btnCls} border border-subtle text-ink hover:border-blue`}>{p.active ? "Deactivate" : "Activate"}</button>
        <button onClick={onDelete} data-testid={`promo-delete-${p.code}`} className={`${btnCls} border border-subtle text-red-700 hover:border-red-300`}>Delete</button>
      </div>
    </div>
  );
}

function PromoReport({ rows }) {
  const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);
  const head = "px-5 py-3 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-faint font-normal";
  const cell = "px-5 py-3.5 text-sm text-ink";
  return (
    <div className="mt-10" data-testid="promo-report">
      <h2 className="font-serif text-2xl tracking-tight text-ink">Performance</h2>
      <p className="mt-1 text-sm text-faint">Paid bookings only. Revenue is the amount customers actually paid (after discount, less refunds).</p>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-subtle bg-surface">
        <table className="w-full min-w-[560px]">
          <thead className="border-b border-subtle">
            <tr><th className={head}>Code</th><th className={head}>Paid bookings</th><th className={head}>Discount given</th><th className={head}>Revenue received</th><th className={head}>Refunded</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-sm text-faint" data-testid="promo-report-empty">No paid bookings with a promo code yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-subtle last:border-0" data-testid={`promo-report-row-${r.code}`}>
                <td className={`${cell} font-mono tracking-[0.08em] text-blue`}>{r.code}</td>
                <td className={cell} data-testid={`promo-report-bookings-${r.code}`}>{r.paid_bookings}</td>
                <td className={cell} data-testid={`promo-report-discount-${r.code}`}>{gbp(r.discount_given)}</td>
                <td className={cell} data-testid={`promo-report-revenue-${r.code}`}>{gbp(r.revenue)}</td>
                <td className={cell} data-testid={`promo-report-refunded-${r.code}`}>{r.refunded_bookings}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 1 && (
            <tfoot className="border-t border-subtle bg-warmcard">
              <tr data-testid="promo-report-totals">
                <td className={`${cell} font-medium`}>Total</td><td className={cell}>{sum("paid_bookings")}</td>
                <td className={cell}>{gbp(sum("discount_given"))}</td><td className={cell}>{gbp(sum("revenue"))}</td><td className={cell}>{sum("refunded_bookings")}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export function DiscountCodes({ flash }) {
  const [promos, setPromos] = useState([]);
  const [report, setReport] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => Promise.all([api.get("/admin/promos"), api.get("/admin/promos/report")])
    .then(([p, r]) => { setPromos(p.data); setReport(r.data); });
  useEffect(() => { load(); }, []);

  const copyLink = async (p) => {
    const link = `${window.location.origin}/book?promo=${encodeURIComponent(p.code)}`;
    try { await navigator.clipboard.writeText(link); flash("Link copied"); } catch { window.prompt("Copy this link:", link); }
  };

  const save = async (body) => {
    if (editing.id) await api.put(`/admin/promos/${editing.id}`, body);
    else await api.post("/admin/promos", body);
    setEditing(null);
    await load();
    flash("Promo code saved");
  };
  const toggle = async (p) => {
    const { id, times_used, created_at, updated_at, ...rest } = p;
    await api.put(`/admin/promos/${id}`, { ...rest, active: !p.active });
    await load();
    flash(p.active ? "Code deactivated" : "Code activated");
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete promo code ${p.code}? Past bookings keep their discount record.`)) return;
    await api.delete(`/admin/promos/${p.id}`);
    await load();
    flash("Promo code deleted");
  };

  return (
    <div data-testid="admin-discount-codes">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-serif text-3xl tracking-tight text-ink">Discount Codes</h1>
        {!editing && (
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="promo-new-button" className={`${btnCls} bg-blue text-canvas hover:bg-blue-hover`}>New code</button>
        )}
      </div>
      <div className="mt-4 rounded-2xl border border-sage/40 bg-sage-light/50 p-5 text-sm leading-relaxed text-body" data-testid="promo-rule-note">
        <strong className="font-medium text-ink">Discounts apply to the cleaning service price only - never to extras.</strong>{" "}
        Extras are always charged at their full price. Your published prices never change; a code only reduces the
        cleaning price for customers who enter it. Usage counts only paid bookings.
      </div>
      {editing && <PromoForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="mt-6 overflow-hidden rounded-2xl border border-subtle bg-surface">
        {promos.length === 0 && <p className="px-5 py-6 text-sm text-faint" data-testid="promo-empty">No promo codes yet.</p>}
        {promos.map((p) => (
          <PromoRow key={p.id} p={p} onEdit={() => setEditing({ ...EMPTY, ...p })} onToggle={() => toggle(p)} onDelete={() => remove(p)} onCopy={() => copyLink(p)} />
        ))}
      </div>
      <PromoReport rows={report} />
    </div>
  );
}
